/**
 * Shared primitives for the three SAPIENT **geometry polygon** map layers —
 * field-of-view / coverage / obscuration (PR5b inc3 + inc4; REQ-UI-070;
 * ADR-032 §4.2). The single, parameterised mechanism the per-kind layers
 * (:file:`fov-layer-impl.ts`, :file:`coverage-layer-impl.ts`,
 * :file:`obscuration-layer-impl.ts`) delegate to, so the ring-closing +
 * antimeridian-unwrap + source/fill/line registration logic exists **once**
 * (CLAUDE.md §4.4 — never duplicate logic across modules).
 *
 * # What the three layers share vs. differ
 *
 * All three render a per-platform set of WGS84 rings as a faint desaturated
 * fill (a ``--geom-*`` token at low opacity) plus a more-saturated outline
 * (a ``--geom-*-stroke`` token) so the shape stays operator-visible once the
 * fill drops to a wash (CLAUDE.md §8.1), painted **below** the snapshot
 * marker layer (markers stay on top per §8.1). They differ in exactly three
 * things, all passed in by the per-kind impl:
 *
 *   1. the MapLibre source / fill-layer / line-layer IDs;
 *   2. the ring **extractor** — how to pull the drawable rings out of a
 *      :type:`StatusGeometry`. FoV yields at most one ring
 *      (``g => g.field_of_view ? [g.field_of_view] : []``); coverage /
 *      obscuration yield a **list** of rings (``g => g.coverage`` /
 *      ``g => g.obscuration``), so each platform contributes *multiple*
 *      polygon features;
 *   3. the resolved fill / stroke colours (the inc1 design-system readers
 *      :func:`getGeomColor` / :func:`getGeomStrokeColor` per kind).
 *
 * # One feature per ring
 *
 * :func:`ringsToPolygonFeatures` emits one single-ring ``Polygon`` feature
 * **per drawable ring** (not per platform): a platform reporting two
 * coverage rings yields two features (``ring_index`` 0 and 1). Each ring is
 * antimeridian-unwrapped then closed (a GeoJSON polygon ring must repeat its
 * first position). Rings with < 3 vertices, and platforms whose extractor
 * yields no rings, contribute nothing.
 *
 * Cross-references:
 *   - REQ-UI-070 (SAPIENT map geometry) / REQ-UI-040 / REQ-UI-046.
 *   - ADR-032 §4.2 (Status geometry data model — FoV single ring; coverage
 *     / obscuration ring lists).
 *   - CLAUDE.md §4.4 (no duplicated logic) / §8.1 (markers above the wash).
 *   - map/antimeridian.ts (toUnwrappedCoordinates) · map/marker-styling.ts
 *     (getGeomColor / getGeomStrokeColor) · map/geometry-store.ts.
 *   - map/fov-layer-impl.ts · map/coverage-layer-impl.ts ·
 *     map/obscuration-layer-impl.ts (the three delegating impls).
 */
import type { GeoJSONSource, Map as MapLibreMap } from "maplibre-gl";
import { toUnwrappedCoordinates } from "@/map/antimeridian";
import { isMapUsable } from "@/map/map-ready";
import { SNAPSHOT_LAYER_ID } from "@/map/snapshot-layer-impl";
import type { StatusGeometry } from "@/map/sse-types";

/**
 * A per-platform geometry entry as the feature builder consumes it — the
 * source ``producer_id`` plus its latest :type:`StatusGeometry`. Matches the
 * :type:`PlatformGeometryEntry` the geometry store yields.
 */
export interface GeometryEntry {
  readonly producer_id: string;
  readonly geometry: StatusGeometry;
}

/**
 * Pull the drawable rings out of one platform's :type:`StatusGeometry`. A
 * ring is a list of ``[lon, lat]`` positions (longitudes normalized to
 * ``[-180, 180]``). FoV returns at most one ring; coverage / obscuration
 * return the full ring list. Returning ``null`` / ``undefined`` rings is
 * fine — they're filtered by :func:`isDrawableRing`.
 */
export type RingExtractor = (geometry: StatusGeometry) => readonly (number[][] | null)[];

/**
 * Identity properties stamped onto every polygon feature. ``producer_id`` so
 * a future selection edge can key on it (markers own platform selection
 * today; the geometry polygons are a passive overlay), ``ring_index`` so a
 * multi-ring platform's features stay individually addressable.
 */
export interface GeometryFeatureProps {
  readonly producer_id: string;
  readonly ring_index: number;
}

/**
 * Close a polygon ring: a GeoJSON ``Polygon`` ring MUST repeat its first
 * position as its last. Appends a copy of the first point when the ring is
 * not already closed (first ≠ last). Rings of < 2 points are returned
 * unchanged (a degenerate ring is filtered upstream, never built into a
 * feature).
 *
 * Operates on ``[lon, lat]`` tuples AFTER the antimeridian unwrap, so the
 * closing point matches the unwrapped first point exactly (closing before
 * unwrap could reintroduce a seam jump on the closing segment).
 */
export function closeRing(ring: readonly [number, number][]): [number, number][] {
  if (ring.length < 2) {
    return ring.map((p) => [p[0], p[1]]);
  }
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  const closed: [number, number][] = ring.map((p) => [p[0], p[1]]);
  if (first[0] !== last[0] || first[1] !== last[1]) {
    closed.push([first[0], first[1]]);
  }
  return closed;
}

/**
 * Unwrap a raw ring (``[lon, lat]`` tuples, longitudes normalized to
 * ``[-180, 180]``) across the antimeridian so a polygon spanning the ±180°
 * seam stays continuous instead of drawing across the whole map. Mirrors how
 * :file:`track-tail-layer-impl.ts` routes its ``LineString`` coords through
 * :func:`toUnwrappedCoordinates` — the ring is mapped to the ``LonLatPoint``
 * shape that helper expects, then back to tuples.
 */
function unwrapRing(ring: readonly number[][]): [number, number][] {
  return toUnwrappedCoordinates(
    ring.map((pt) => ({ longitude_deg: pt[0]!, latitude_deg: pt[1]! })),
  );
}

/**
 * Whether a raw ring is drawable: a non-null array with at least 3 distinct
 * vertices (the minimum for a polygon). A ``null`` ring (the platform
 * reported none) or a too-short ring yields no feature.
 */
function isDrawableRing(ring: number[][] | null | undefined): ring is number[][] {
  return Array.isArray(ring) && ring.length >= 3;
}

/**
 * Build the GeoJSON source payload — one single-ring ``Polygon`` feature
 * **per drawable ring** across all platforms. For each entry the
 * ``extractRings`` callback yields that platform's rings; each drawable ring
 * (≥ 3 points) is antimeridian-unwrapped then closed and emitted as its own
 * feature, tagged with ``{ producer_id, ring_index }`` (``ring_index`` is
 * the index within that platform's ring list, so a 2-ring platform yields
 * features with indices 0 and 1). Null / too-short rings are skipped, and a
 * platform whose extractor yields no rings contributes nothing.
 *
 * This is the shared core for the FoV (one ring), coverage (ring list), and
 * obscuration (ring list) layers — the only per-kind variation is the
 * ``extractRings`` callback (CLAUDE.md §4.4). Exported for unit testability.
 */
export function ringsToPolygonFeatures(
  entries: readonly GeometryEntry[],
  extractRings: RingExtractor,
): GeoJSON.FeatureCollection<GeoJSON.Polygon, GeometryFeatureProps> {
  const features: GeoJSON.Feature<GeoJSON.Polygon, GeometryFeatureProps>[] = [];
  for (const { producer_id, geometry } of entries) {
    const rings = extractRings(geometry);
    rings.forEach((ring, ring_index) => {
      if (!isDrawableRing(ring)) {
        return;
      }
      const coordinates = closeRing(unwrapRing(ring));
      features.push({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [coordinates] },
        properties: { producer_id, ring_index },
      });
    });
  }
  return { type: "FeatureCollection", features };
}

/**
 * Parameters for :func:`attachGeometryPolygonLayer` — the per-kind IDs, the
 * already-built feature collection, and the theme-resolved paints.
 */
export interface AttachGeometryPolygonLayerParams {
  /** MapLibre GeoJSON source ID for this kind's polygons. */
  readonly sourceId: string;
  /** MapLibre fill-layer ID (the faint wash). */
  readonly fillLayerId: string;
  /** MapLibre line-layer ID (the saturated outline). */
  readonly lineLayerId: string;
  /** The feature collection to set on the source (one feature per ring). */
  readonly features: GeoJSON.FeatureCollection<GeoJSON.Polygon, GeometryFeatureProps>;
  /** Resolved fill colour (``getGeomColor(kind)``). */
  readonly fillColor: string;
  /** Resolved stroke colour (``getGeomStrokeColor(kind)``). */
  readonly strokeColor: string;
  /** Fill opacity for the wash (within ~0.12–0.2). */
  readonly fillOpacity: number;
  /** Outline width in screen px. */
  readonly lineWidth: number;
}

/** Parameters for :func:`detachGeometryPolygonLayer` — the per-kind IDs. */
export interface DetachGeometryPolygonLayerParams {
  readonly sourceId: string;
  readonly fillLayerId: string;
  readonly lineLayerId: string;
}

/**
 * Register one geometry kind's source + fill layer + line layer (idempotent:
 * later calls ``setData`` + re-apply the theme-resolved paint colours). Both
 * layers are inserted **below** the snapshot marker layer via ``beforeId``
 * so the markers paint on top of the geometry wash (CLAUDE.md §8.1).
 *
 * The fill layer paints the faint wash (``fillColor`` at ``fillOpacity``);
 * the line layer paints the saturated outline (``strokeColor`` at
 * ``lineWidth``). On re-attach both paints are refreshed so a theme flip
 * (REQ-UI-040) takes effect without re-mounting.
 *
 * The three per-kind impls call this with their own IDs + colours; the
 * mechanism lives here once (CLAUDE.md §4.4).
 */
export function attachGeometryPolygonLayer(
  map: MapLibreMap,
  params: AttachGeometryPolygonLayerParams,
): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  const {
    sourceId,
    fillLayerId,
    lineLayerId,
    features,
    fillColor,
    strokeColor,
    fillOpacity,
    lineWidth,
  } = params;

  const existing = map.getSource(sourceId) as GeoJSONSource | undefined;
  if (existing) {
    existing.setData(features);
    // Refresh both paints so a theme flip carries through (REQ-UI-040).
    if (map.getLayer(fillLayerId)) {
      map.setPaintProperty(fillLayerId, "fill-color", fillColor);
    }
    if (map.getLayer(lineLayerId)) {
      map.setPaintProperty(lineLayerId, "line-color", strokeColor);
    }
    return;
  }

  map.addSource(sourceId, { type: "geojson", data: features });

  // Snapshot marker layer paints above the wash. ``beforeId`` is a hint —
  // MapLibre falls back to "on top" if the named layer doesn't exist
  // (teardown race). Operator-visibility per §8.1: the geometry still
  // renders even if the marker layer briefly disappears.
  const beforeId = map.getLayer(SNAPSHOT_LAYER_ID) ? SNAPSHOT_LAYER_ID : undefined;

  map.addLayer(
    {
      id: fillLayerId,
      type: "fill",
      source: sourceId,
      paint: {
        "fill-color": fillColor,
        "fill-opacity": fillOpacity,
      },
    },
    beforeId,
  );
  map.addLayer(
    {
      id: lineLayerId,
      type: "line",
      source: sourceId,
      layout: { "line-join": "round" },
      paint: {
        "line-color": strokeColor,
        "line-width": lineWidth,
      },
    },
    beforeId,
  );
}

/**
 * Tear down one geometry kind's fill + line layers and source (layers before
 * source per MapLibre's documented ordering — a source can't be removed
 * while a layer references it). No-op when nothing is registered.
 */
export function detachGeometryPolygonLayer(
  map: MapLibreMap,
  params: DetachGeometryPolygonLayerParams,
): void {
  // No-op safely on a removed / not-ready map (teardown race) — see
  // :file:`map/map-ready.ts`; CLAUDE.md §8.1 fail-safe.
  if (!isMapUsable(map)) {
    return;
  }
  const { sourceId, fillLayerId, lineLayerId } = params;
  if (map.getLayer(fillLayerId)) {
    map.removeLayer(fillLayerId);
  }
  if (map.getLayer(lineLayerId)) {
    map.removeLayer(lineLayerId);
  }
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }
}
